
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import AnalysisContent from './AnalysisContent.vue';
import { contextSyncService } from '@/services/contextSync';
import {
  SalesData,
  ProfitAnalysis,
  VariantAnalysisData,
  VariantSummary,
  ProductAnalysisParameters,
  ProductAnalysisChatResponse,
} from '@/types/productAnalysis';

@Component({
  name: 'ProductAnalysisDisplay',
  components: {
    AnalysisContent,
  },
})
export default class ProductAnalysisDisplay extends Vue {
  // =========================================================================
  // Props
  // =========================================================================
  @Prop({ default: null }) readonly chatResponse!: ProductAnalysisChatResponse | null;
  @Prop({ default: () => ({}) }) readonly actionParameters!: ProductAnalysisParameters;

  // =========================================================================
  // Local State
  // =========================================================================
  private activeTab = 0;

  // =========================================================================
  // Computed Getters
  // =========================================================================

  /** Product name from action parameters */
  get productName(): string {
    return this.actionParameters?.product_name || 'Unknown Product';
  }

  /** SKU from action parameters (single variant mode) */
  get sku(): string {
    return this.actionParameters?.sku || '';
  }

  /** Variant name from action parameters (single variant mode) */
  get variantName(): string {
    return this.actionParameters?.variant_name || '';
  }

  /** Analysis period label */
  get analysisPeriod(): string {
    return this.actionParameters?.analysis_period || 'Last 90 days';
  }

  /** Check if we have multiple variants data */
  get hasMultipleVariants(): boolean {
    return (this.actionParameters?.variants_data?.length || 0) > 1;
  }

  /** Get variants data array */
  get variantsData(): VariantAnalysisData[] {
    return this.actionParameters?.variants_data || [];
  }

  /** Get variant count */
  get variantCount(): number {
    return this.variantsData.length;
  }

  /** Get variants summary for aggregated view */
  get variantsSummary(): VariantSummary[] {
    // Use provided summary or build from variants_data
    if (this.actionParameters?.variants_summary) {
      return this.actionParameters.variants_summary;
    }
    return this.variantsData.map((v) => ({
      variant_name: v.variant_name || 'Default',
      sku: v.sku || '',
      total_units: v.sales_data?.total_units || 0,
      total_revenue: v.sales_data?.total_revenue || 0,
      gross_margin_pct: v.profit_analysis?.gross_margin_pct || 0,
    }));
  }

  /** Aggregated sales data (for "All Variants" tab) */
  get aggregatedSalesData(): SalesData {
    return this.actionParameters?.sales_data || {
      monthly_data: [],
      avg_monthly_units: 0,
      total_units: 0,
      total_revenue: 0,
    };
  }

  /** Aggregated profit analysis (for "All Variants" tab) */
  get aggregatedProfitAnalysis(): ProfitAnalysis {
    return this.actionParameters?.profit_analysis || {
      avg_unit_cost: 0,
      avg_unit_price: 0,
      gross_margin_pct: 0,
      total_cogs: 0,
      total_profit: 0,
    };
  }

  /** Aggregated insights (for "All Variants" tab) */
  get aggregatedInsights(): string[] {
    return this.actionParameters?.insights || [];
  }

  /** Sales data (single variant mode) */
  get salesData(): SalesData {
    return this.actionParameters?.sales_data || {
      monthly_data: [],
      avg_monthly_units: 0,
      total_units: 0,
      total_revenue: 0,
    };
  }

  /** Profit analysis (single variant mode) */
  get profitAnalysis(): ProfitAnalysis {
    return this.actionParameters?.profit_analysis || {
      avg_unit_cost: 0,
      avg_unit_price: 0,
      gross_margin_pct: 0,
      total_cogs: 0,
      total_profit: 0,
    };
  }

  /** Insights (single variant mode) */
  get insights(): string[] {
    return this.actionParameters?.insights || [];
  }

  // =========================================================================
  // Lifecycle Hooks
  // =========================================================================

  public mounted() {
    this.syncScreenContext();
  }

  @Watch('actionParameters', { deep: true })
  onActionParametersChanged() {
    this.syncScreenContext();
  }

  /**
   * Sync screen context to contextSyncService for automatic context attachment.
   * Called on mount and when actionParameters change.
   */
  private syncScreenContext(): void {
    if (!contextSyncService.getSessionId()) {
      return;
    }

    if (!this.actionParameters?.product_name) {
      return; // No data to sync
    }

    contextSyncService.updateScreenContext({
      screen_name: 'product_analysis',
      screen_path: '/purchase-assistant',
      screen_params: {
        product_name: this.productName,
        sku: this.sku || undefined,
        variant_name: this.variantName || undefined,
      },
      component_state: {
        screen_type: 'product_analysis',
        screen_data: {
          product_name: this.productName,
          sku: this.sku || undefined,
          variant_name: this.variantName || undefined,
          analysis_period: this.analysisPeriod,
          has_multiple_variants: this.hasMultipleVariants,
          variant_count: this.variantCount,
          // Sales summary
          sales_summary: {
            total_units: this.salesData?.total_units ?? 0,
            total_revenue: this.salesData?.total_revenue ?? 0,
            avg_monthly_units: this.salesData?.avg_monthly_units ?? 0,
          },
          // Profit summary
          profit_summary: {
            gross_margin_pct: this.profitAnalysis?.gross_margin_pct ?? 0,
            total_profit: this.profitAnalysis?.total_profit ?? 0,
            avg_unit_cost: this.profitAnalysis?.avg_unit_cost ?? 0,
            avg_unit_price: this.profitAnalysis?.avg_unit_price ?? 0,
          },
          // Insights
          insights: this.insights,
        },
      },
    });
  }
}
